import { useState, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import { createKfaQuestion, updateKfaQuestion, patchKfaStatus, CreateKfaQuestionPayload } from '../../../../store/slices/adminSlice';
import { useSelectedLocation } from '../../../../hooks';
import { createApiClient } from '../../../../api/apiClient';
import {
  LOCATION_TYPE_OPTIONS,
  ORGANIZATION_TYPE_OPTIONS,
  THREE_PL_OPTIONS,
  SPARC_CATEGORY_OPTIONS,
  FREQUENCY_OPTIONS,
  SPARC_SECTION_OPTIONS,
} from './kfaDropdownOptions';
import { getValidationState, REQUIRED_FIELDS_MESSAGE } from './validationUtils';
import { MSOption } from './KfaMultiSelectDropdown';
import { KfaEditItem } from './KfaAddOverlay';

export interface KfaValidationErrors {
  keyFocusArea: boolean;
  question: boolean;
  locationType: boolean;
  organizationType: boolean;
  threeplStatus: boolean;
  category: boolean;
  moduleSelection: boolean;
  leadershipZone: boolean;
  leadershipFrequency: boolean;
  sparcZone: boolean;
  sparcSection: boolean;
}

const MODULE_NAME_MAP: Record<string, string> = {
  'Observations': 'observations',
  'Shift Handover': 'shiftHandover',
  'Leadership Walk': 'leadershipWalk',
  'SPARC Walk': 'sparcWalk',
  'NHO': 'nho',
  'Racking Inspection': 'rackingInspection',
  'Site Evaluation': 'siteEvaluation',
};

export const DEFAULT_MODULES = {
  observations: false,
  shiftHandover: false,
  leadershipWalk: false,
  sparcWalk: false,
  nho: false,
  rackingInspection: false,
  siteEvaluation: false,
};

const MODULE_KEY_TO_NAME: Record<string, string> = {
  shiftHandover: 'Shift Handover',
  leadershipWalk: 'Leadership Walk',
  sparcWalk: 'SPARC Walk',
  observations: 'Observations',
  nho: 'NHO',
  rackingInspection: 'Racking Inspection',
  siteEvaluation: 'Site Evaluation',
};

export const MODULE_DISPLAY_NAMES: Record<string, string> = {
  shiftHandover: 'Shift Handover',
  leadershipWalk: 'Leadership Walk',
  sparcWalk: 'SPARC Walk',
};

/** Find a dropdown option by id. Returns null if not found. */
export const findOption = <T extends { id: string | number; displayText: string }>(
  options: T[],
  value: string | number | undefined | null,
): T | null => {
  if (value === undefined || value === null || value === '') return null;
  return options.find((o) => String(o.id).toLowerCase() === String(value).toLowerCase()) ?? null;
};

/** Find all matching options from a comma-separated string or array. */
export const findOptions = <T extends { id: string | number; displayText: string }>(
  options: T[],
  value: string | string[] | undefined | null,
): T[] => {
  if (!value) return [];
  const parts = Array.isArray(value)
    ? value
    : String(value).split(',').map((s) => s.trim()).filter(Boolean);
  return options.filter((o) =>
    parts.some((v) => String(o.id).toLowerCase() === v.toLowerCase()),
  );
};

const EMPTY_FIELD_ERRORS: KfaValidationErrors = {
  keyFocusArea: false,
  question: false,
  locationType: false,
  organizationType: false,
  threeplStatus: false,
  category: false,
  moduleSelection: false,
  leadershipZone: false,
  leadershipFrequency: false,
  sparcZone: false,
  sparcSection: false,
};

interface UseKfaFormParams {
  visible: boolean;
  mode: 'create' | 'edit';
  selectedKfa?: KfaEditItem;
  selectedModule?: string;
  onDismiss: () => void;
  onSaveSuccess?: (moduleNames?: string[]) => void;
}

export function useKfaForm({
  visible,
  mode,
  selectedKfa,
  selectedModule,
  onDismiss,
  onSaveSuccess,
}: UseKfaFormParams) {
  const dispatch = useDispatch<AppDispatch>();
  const isSaving = useSelector((state: RootState) => state.configuration.keyFocusAreas.isSaving);
  const saveError = useSelector((state: RootState) => state.configuration.keyFocusAreas.error);
  const selectedLocation = useSelectedLocation();

  const [keyFocusArea, setKeyFocusArea] = useState('');
  const [question, setQuestion] = useState('');
  const [locationType, setLocationType] = useState<MSOption[]>([]);
  const [organizationType, setOrganizationType] = useState<MSOption[]>([]);
  const [threeplStatus, setThreeplStatus] = useState<any>(null);
  const [category, setCategory] = useState<MSOption[]>([]);
  const [modules, setModules] = useState({ ...DEFAULT_MODULES });
  const [observationType, setObservationType] = useState<any>(null);
  const [section, setSection] = useState<any>(null);
  const [roles, setRoles] = useState<any>(null);
  const [leadershipZone, setLeadershipZone] = useState<MSOption[]>([]);
  const [leadershipFrequency, setLeadershipFrequency] = useState<MSOption[]>([]);
  const [statusEnabled, setStatusEnabled] = useState<boolean>(true);
  const [sparcZone, setSparcZone] = useState<MSOption[]>([]);
  const [sparcSection, setSparcSection] = useState<any>(null);
  const [availableZones, setAvailableZones] = useState<{ id: string; displayText: string; ZONE_ID: number }[]>([]);
  const [isFetchingZones, setIsFetchingZones] = useState(false);
  const [nhoTouchType, setNhoTouchType] = useState<any>(null);
  const [nhoSection, setNhoSection] = useState<any>(null);
  const [rackingSection, setRackingSection] = useState<any>(null);
  const [siteEvaluationSection, setSiteEvaluationSection] = useState<any>(null);

  const isTextLocked = mode === 'edit' && !!selectedKfa?.createdAt
    && (Date.now() - new Date(selectedKfa.createdAt).getTime()) > 24 * 60 * 60 * 1000;

  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<KfaValidationErrors>({ ...EMPTY_FIELD_ERRORS });
  const isDirtyRef = useRef(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingUncheckModule, setPendingUncheckModule] = useState<string | null>(null);

  const hasModuleData = (moduleKey: string): boolean => {
    switch (moduleKey) {
      case 'leadershipWalk': return !!(leadershipZone.length > 0 || leadershipFrequency.length > 0);
      case 'sparcWalk':      return !!(sparcZone.length > 0 || sparcSection);
      default:               return false;
    }
  };

  const clearModuleFields = (moduleKey: string) => {
    switch (moduleKey) {
      case 'leadershipWalk':
        setLeadershipZone([]);
        setLeadershipFrequency([]);
        break;
      case 'sparcWalk':
        setSparcZone([]);
        setSparcSection(null);
        break;
      default: break;
    }
  };

  const syncModuleSelectionError = (nextModules: typeof modules) => {
    isDirtyRef.current = true;
    const hasAny = Object.values(nextModules).some(Boolean);
    setFieldErrors((prev) => ({ ...prev, moduleSelection: !hasAny }));
    if (hasAny) setValidationMessage(null);
  };

  const handleModuleUncheck = (moduleKey: string) => {
    if (hasModuleData(moduleKey)) {
      setPendingUncheckModule(moduleKey);
    } else {
      clearModuleFields(moduleKey);
      const nextModules = { ...modules, [moduleKey]: false };
      setModules(nextModules);
      syncModuleSelectionError(nextModules);
    }
  };

  const clearFieldError = (field: keyof KfaValidationErrors) => {
    isDirtyRef.current = true;
    setFieldErrors((prev) => ({ ...prev, [field]: false }));
    setValidationMessage(null);
  };

  const validateForm = () => {
    const hasAnyModuleSelected = Object.values(modules).some(Boolean);
    const onlyShiftHandover = modules.shiftHandover && !modules.leadershipWalk && !modules.sparcWalk;

    // Shift Handover only: at least one of KFA or Question must be filled
    const shiftHandoverBothEmpty = onlyShiftHandover && !keyFocusArea.trim() && !question.trim();

    // All other modules (or mixed): both KFA and Question are required
    const kfaEmpty = !onlyShiftHandover && !keyFocusArea.trim();
    const questionEmpty = !onlyShiftHandover && !question.trim();

    const { fieldErrors: nextErrors, hasErrors } = getValidationState<keyof KfaValidationErrors>({
      keyFocusArea: onlyShiftHandover ? shiftHandoverBothEmpty : kfaEmpty,
      question: onlyShiftHandover ? shiftHandoverBothEmpty : questionEmpty,
      locationType: locationType.length === 0,
      organizationType: organizationType.length === 0,
      threeplStatus: !threeplStatus,
      category: category.length === 0,
      moduleSelection: !hasAnyModuleSelected,
      leadershipZone: modules.leadershipWalk && leadershipZone.length === 0,
      leadershipFrequency: modules.leadershipWalk && leadershipFrequency.length === 0,
      sparcZone: modules.sparcWalk && sparcZone.length === 0,
      sparcSection: modules.sparcWalk && !sparcSection,
    });
    setFieldErrors(nextErrors);
    if (hasErrors) {
      if (shiftHandoverBothEmpty) {
        setValidationMessage('Must enter Key Focus Area or Question');
      } else if (kfaEmpty || questionEmpty) {
        setValidationMessage('Key Focus Area and Question are required');
      } else {
        setValidationMessage(REQUIRED_FIELDS_MESSAGE);
      }
      return false;
    }
    return true;
  };

  // Pre-select zones once they are fetched (edit mode)
  useEffect(() => {
    if (mode !== 'edit' || !selectedKfa || availableZones.length === 0) return;
    const zoneIds = selectedKfa.zoneIds ?? [];
    if (zoneIds.length === 0) return;
    const matchedZones = availableZones.filter((z) =>
      zoneIds.some((id) => String(z.id) === String(id))
    );
    if (matchedZones.length > 0) {
      if (modules.leadershipWalk) setLeadershipZone(matchedZones);
      if (modules.sparcWalk) setSparcZone(matchedZones);
    }
  }, [availableZones, mode, selectedKfa]);

  // Fetch zones when Leadership Walk or SPARC Walk is active
  useEffect(() => {
    const needsZones = modules.leadershipWalk || modules.sparcWalk;
    if (!needsZones || !visible) return;
    const locationName =
      (selectedLocation as any)?.displayText ||
      (selectedLocation as any)?.name ||
      '';
    if (!locationName) return;

    const fetchZones = async () => {
      setIsFetchingZones(true);
      try {
        const api = createApiClient();
        const response = await api.get(
          `/leadership-walks/zones?location=${encodeURIComponent(locationName)}`
        );
        if (Array.isArray(response.data)) {
          setAvailableZones(
            response.data.map((z: any) => ({
              id: String(z.ZONE_ID),
              displayText: z.ZONE,
              ZONE_ID: Number(z.ZONE_ID),
            }))
          );
        }
      } catch {
        // Non-critical — zones will just stay empty
      } finally {
        setIsFetchingZones(false);
      }
    };
    fetchZones();
  }, [visible, modules.leadershipWalk, modules.sparcWalk, selectedLocation]);

  // Scroll-lock the page while the overlay is open (web only)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (visible) {
      const scrollY = (window as any).scrollY || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      (document.body.dataset as any).kfaOverlayScrollY = String(scrollY);
    } else {
      const savedY = parseInt((document.body.dataset as any).kfaOverlayScrollY || '0', 10);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedY);
    }
    return () => {
      if (Platform.OS === 'web') {
        const savedY = parseInt((document.body.dataset as any).kfaOverlayScrollY || '0', 10);
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, savedY);
      }
    };
  }, [visible]);

  // Reset / pre-fill fields when overlay opens/closes
  useEffect(() => {
    if (!visible) {
      setKeyFocusArea('');
      setQuestion('');
      setLocationType([]);
      setOrganizationType([]);
      setThreeplStatus(null);
      setCategory([]);
      setModules({ ...DEFAULT_MODULES });
      setLeadershipZone([]);
      setLeadershipFrequency([]);
      setSparcZone([]);
      setSparcSection(null);
      setStatusEnabled(true);
      setObservationType(null);
      setSection(null);
      setRoles(null);
      setNhoTouchType(null);
      setNhoSection(null);
      setRackingSection(null);
      setSiteEvaluationSection(null);
      setAvailableZones([]);
      setValidationMessage(null);
      setFieldErrors({ ...EMPTY_FIELD_ERRORS });
      isDirtyRef.current = false;
      return;
    }

    if (mode === 'edit' && selectedKfa) {
      setKeyFocusArea(selectedKfa.negativeBehavior ?? '');
      setQuestion(selectedKfa.question ?? '');
      setCategory(findOptions(SPARC_CATEGORY_OPTIONS, selectedKfa.sparcCategory));
      setLocationType(findOptions(LOCATION_TYPE_OPTIONS, selectedKfa.locationType));
      setOrganizationType(findOptions(ORGANIZATION_TYPE_OPTIONS, selectedKfa.organizationType));
      setThreeplStatus(
        selectedKfa.is3pl !== undefined
          ? findOption(THREE_PL_OPTIONS, selectedKfa.is3pl ? 'Yes' : 'No')
          : null,
      );
      setLeadershipFrequency(findOptions(FREQUENCY_OPTIONS, selectedKfa.frequency));
      setSparcSection(findOption(SPARC_SECTION_OPTIONS, selectedKfa.sparcSection));
      setStatusEnabled(selectedKfa.status !== false);
      const moduleNames = (selectedKfa.modules ?? '').split(',').map((m) => m.trim()).filter(Boolean);
      const prefillModules = { ...DEFAULT_MODULES };
      moduleNames.forEach((name) => {
        const key = MODULE_NAME_MAP[name];
        if (key) (prefillModules as any)[key] = true;
      });
      setModules(prefillModules);
    } else {
      setKeyFocusArea('');
      setQuestion('');
      setCategory([]);
      setLocationType([]);
      setOrganizationType([]);
      setThreeplStatus(null);
      setLeadershipFrequency([]);
      setModules({ ...DEFAULT_MODULES });
      setValidationMessage(null);
      setFieldErrors({ ...EMPTY_FIELD_ERRORS });
    }

    if (mode === 'create' && selectedModule && selectedModule !== 'main') {
      const key = MODULE_NAME_MAP[selectedModule];
      const locked = { ...DEFAULT_MODULES };
      if (key) (locked as any)[key] = true;
      setModules(locked);
    }
  }, [visible, mode, selectedKfa, selectedModule]);

  const buildPayload = (): CreateKfaQuestionPayload => {
    const activeModuleNames = (Object.entries(modules) as [string, boolean][])
      .filter(([, active]) => active)
      .map(([key]) => MODULE_KEY_TO_NAME[key])
      .filter(Boolean);

    const topicType = (selectedModule && selectedModule !== 'main')
      ? selectedModule
      : (activeModuleNames[0] ?? '');
    const additionalModules: string[] = activeModuleNames;

    const zoneIds: number[] = [];
    leadershipZone.forEach((zone) => {
      const zoneId = Number(zone.id);
      if (!zoneIds.includes(zoneId)) zoneIds.push(zoneId);
    });
    sparcZone.forEach((zone) => {
      const zoneId = Number(zone.id);
      if (!zoneIds.includes(zoneId)) zoneIds.push(zoneId);
    });

    return {
      question: question.trim(),
      sparcCategory: category.map((x) => String(x.id)).join(','),
      locationType: locationType.map((x) => String(x.id)).join(','),
      negativeBehavior: keyFocusArea.trim(),
      details: '',
      detailLink: '',
      frequency: leadershipFrequency.map((x) => x.id).join(','),
      topicType,
      is3pl: threeplStatus?.id === 'Yes',
      organizationType: organizationType.map((x) => String(x.id)).join(','),
      additionalModules,
      zoneIds,
      section: sparcSection?.id ?? '',
    };
  };

  const buildPayloadWithLog = (): CreateKfaQuestionPayload => {
    const p = buildPayload();
    console.log('[KFA] buildPayload →', JSON.stringify(p));
    return p;
  };

  type SaveAction = (payload: CreateKfaQuestionPayload) => any;
  const SAVE_ACTIONS: Record<'create' | 'edit', SaveAction> = {
    create: (payload) => createKfaQuestion(payload),
    edit:   (payload) => updateKfaQuestion({ id: selectedKfa!.id, ...payload }),
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setValidationMessage(null);
    const payload = buildPayloadWithLog();
    const actionKey: 'create' | 'edit' = mode === 'edit' && selectedKfa ? 'edit' : 'create';
    try {
      await dispatch(SAVE_ACTIONS[actionKey](payload)).unwrap();
      if (mode === 'edit' && selectedKfa?.id) {
        await dispatch(patchKfaStatus({ id: selectedKfa.id, status: statusEnabled })).unwrap();
      }
      onDismiss();
      if (mode !== 'edit') {
        const activeModuleNames = (Object.entries(modules) as [string, boolean][])
          .filter(([, active]) => active)
          .map(([key]) => MODULE_KEY_TO_NAME[key])
          .filter(Boolean);
        onSaveSuccess?.(activeModuleNames);
      } else {
        onSaveSuccess?.();
      }
    } catch (_err) {
      // Error surfaced via saveError from Redux state
    }
  };

  const handleCancel = () => {
    if (isDirtyRef.current) {
      setShowDiscardConfirm(true);
    } else {
      onDismiss();
    }
  };

  return {
    // state
    keyFocusArea, setKeyFocusArea,
    question, setQuestion,
    locationType, setLocationType,
    organizationType, setOrganizationType,
    threeplStatus, setThreeplStatus,
    category, setCategory,
    modules, setModules,
    observationType, setObservationType,
    section, setSection,
    roles, setRoles,
    leadershipZone, setLeadershipZone,
    leadershipFrequency, setLeadershipFrequency,
    statusEnabled, setStatusEnabled,
    sparcZone, setSparcZone,
    sparcSection, setSparcSection,
    availableZones,
    isFetchingZones,
    nhoTouchType, setNhoTouchType,
    nhoSection, setNhoSection,
    rackingSection, setRackingSection,
    siteEvaluationSection, setSiteEvaluationSection,
    isTextLocked,
    validationMessage,
    fieldErrors,
    isDirtyRef,
    showDiscardConfirm, setShowDiscardConfirm,
    pendingUncheckModule, setPendingUncheckModule,
    isSaving,
    saveError,
    // handlers
    handleSave,
    handleCancel,
    clearFieldError,
    syncModuleSelectionError,
    handleModuleUncheck,
    clearModuleFields,
  };
}
